import { Fragment, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { db, isAbortError } from '../../lib/supabase'
import { formatDate } from '../../lib/dateUtils'
import { ErrorAlert } from '../../components/ui'

export default function ContactSubmissions() {
  const { t, i18n } = useTranslation('admin')
  const locale = i18n.language?.startsWith('zh') ? 'zh-CN' : 'en'
  const [submissions, setSubmissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedId, setExpandedId] = useState(null)

  useEffect(() => {
    loadSubmissions()
  }, [])

  const loadSubmissions = async () => {
    setLoading(true)
    try {
      const data = await db.getContactSubmissions()
      setSubmissions(data)
      setError(null)
    } catch (err) {
      if (isAbortError(err)) return
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const filtered = submissions.filter(s => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase().trim()
    return (
      s.name.toLowerCase().includes(q) ||
      s.email.toLowerCase().includes(q) ||
      s.message.toLowerCase().includes(q)
    )
  })

  const unreadCount = submissions.filter(s => !s.is_read).length

  const toggleExpand = (id) => {
    const isOpening = expandedId !== id
    setExpandedId(prev => prev === id ? null : id)

    // Auto-mark as read when expanding an unread message
    if (isOpening) {
      const submission = submissions.find(s => s.id === id)
      if (submission && !submission.is_read) {
        // Optimistic local update
        setSubmissions(prev => prev.map(s => s.id === id ? { ...s, is_read: true } : s))
        db.markContactRead(id).catch(() => {
          // Revert on failure
          setSubmissions(prev => prev.map(s => s.id === id ? { ...s, is_read: false } : s))
        })
      }
    }
  }

  const handleDelete = async (e, id) => {
    e.stopPropagation()
    if (!confirm(t('contactSubmissions.confirmDelete'))) return

    // Optimistic removal
    const prev = submissions
    setSubmissions(submissions.filter(s => s.id !== id))
    if (expandedId === id) setExpandedId(null)

    try {
      await db.deleteContactSubmission(id)
    } catch (err) {
      setSubmissions(prev)
      setError(err.message)
    }
  }

  if (loading) {
    return <div className="text-center py-12 text-gray-500">{t('contactSubmissions.loadingMessages')}</div>
  }

  return (
    <div className="animate-fadeIn">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-2xl font-medium text-gray-800">
              {t('contactSubmissions.title')}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {searchQuery
                ? t('contactSubmissions.filteredCount', { filtered: filtered.length, total: submissions.length })
                : t('contactSubmissions.messageCount', { count: submissions.length })
              }
              {unreadCount > 0 && (
                <span className="ml-2 text-amber-600 font-semibold">
                  {t('contactSubmissions.unread', { count: unreadCount })}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="relative max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('contactSubmissions.searchPlaceholder')}
            className="w-full pl-9 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <ErrorAlert message={error} onDismiss={() => setError(null)} />

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {submissions.length === 0 ? t('contactSubmissions.noMessages') : t('contactSubmissions.noSearchResults')}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('contactSubmissions.tableHeaders.name')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('contactSubmissions.tableHeaders.email')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('contactSubmissions.tableHeaders.preview')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('contactSubmissions.tableHeaders.received')}</th>
                <th className="text-right px-5 py-3 text-sm font-semibold text-gray-600">{t('contactSubmissions.tableHeaders.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(s => (
                <Fragment key={s.id}>
                  <tr
                    className={`hover:bg-gray-50 transition-colors cursor-pointer ${!s.is_read ? 'bg-primary-50/30' : ''}`}
                    onClick={() => toggleExpand(s.id)}
                  >
                    <td className="px-5 py-4 text-gray-800">
                      <div className="flex items-center gap-2">
                        {!s.is_read && (
                          <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" />
                        )}
                        <span className={!s.is_read ? 'font-semibold' : 'font-medium'}>{s.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-gray-600 text-sm">{s.email}</td>
                    <td className={`px-5 py-4 text-sm max-w-xs truncate ${!s.is_read ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                      {s.message.length > 80 ? s.message.slice(0, 80) + '\u2026' : s.message}
                    </td>
                    <td className="px-5 py-4 text-gray-500 text-sm whitespace-nowrap">
                      {formatDate(s.created_at, locale)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleExpand(s.id) }}
                          className="px-3 py-1.5 text-xs font-semibold text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                        >
                          {expandedId === s.id ? t('contactSubmissions.collapse') : t('contactSubmissions.read')}
                        </button>
                        <button
                          onClick={(e) => handleDelete(e, s.id)}
                          className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          {t('contactSubmissions.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === s.id && (
                    <tr className="bg-primary-50/40">
                      <td colSpan={5} className="px-5 py-4">
                        <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed border-l-4 border-primary-300 pl-4">
                          {s.message}
                        </div>
                        <a
                          href={`mailto:${s.email}`}
                          className="inline-block mt-3 text-xs font-semibold text-primary-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t('contactSubmissions.replyTo', { email: s.email })}
                        </a>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
